import React, { useEffect, useState } from "react";
import {
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import {
  departmentsTooltip,
  golfTooltipHeaders,
  spaTooltipHeaders,
} from "../../../constants/TooltipInformation";
import AlertComponent from "../../subcomponents/AlertComponent";

const SpaServiceGroups = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [serviceGroupCode, setServiceGroupCode] = useState<string | null>(null);
  const [serviceGroupName, setServiceGroupName] = useState<string | null>(null);
  const [codeFieldError, setCodeFieldError] = useState<boolean>(false);
  const [codeFieldErrorMessage, setCodeFieldErrorMessage] =
    useState<string>("");
  const [nameFieldError, setNameFieldError] = useState<boolean>(false);
  const [nameFieldErrorMessage, setNameFieldErrorMessage] =
    useState<string>("");

  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const validateServiceGroupFields = (): boolean => {
    if (!serviceGroupCode || serviceGroupCode.length === 0) {
      setAlertMessage("Service Group Code is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setCodeFieldError(false);
    setCodeFieldErrorMessage("");
    if (
      !validateString(
        serviceGroupCode,
        validationLength.serviceGroupCode,
        validationType.CODE,
      )
    ) {
      setCodeFieldError(true);
      setCodeFieldErrorMessage(validationMessages.serviceGroupCode);
      return false;
    }

    if (!serviceGroupName || serviceGroupName.length === 0) {
      setAlertMessage("Service Group Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameFieldError(false);
    setNameFieldErrorMessage("");
    if (
      !validateString(
        serviceGroupName,
        validationLength.serviceGroupName,
        validationType.NAME,
      )
    ) {
      setNameFieldError(true);
      setNameFieldErrorMessage(validationMessages.serviceGroupName);
      return false;
    }

    const listForDuplicate = selectedId
      ? items.filter((i: any) => String(i.id) !== String(selectedId))
      : items;
    if (
      checkDuplicate(
        listForDuplicate,
        DNDFieldsEnum.SERVICEGROUPCODE,
        serviceGroupCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);
  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const updateItem = () => {
    if (!validateServiceGroupFields()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.serviceGroups} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateServiceGroupFields()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setServiceGroupCode(list["Service Group Code"] ?? null);
      setServiceGroupName(list["Service Group Name"] ?? null);
    } else {
      setServiceGroupCode(null);
      setServiceGroupName(null);
      setCodeFieldError(false);
      setCodeFieldErrorMessage("");
      setNameFieldError(false);
      setNameFieldErrorMessage("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = {
    "Service Group Code": serviceGroupCode,
    "Service Group Name": serviceGroupName,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Service Groups
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.serviceGroupHeader ??
                  golfTooltipHeaders?.categoryGroup ??
                  "Service Groups",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px" flexWrap="wrap">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Service Group Code</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.serviceGroupServiceGroupCode,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              placeholder="Service Group Code"
              value={serviceGroupCode ?? ""}
              error={codeFieldError}
              helperText={codeFieldError ? codeFieldErrorMessage : ""}
              onChange={(e) => setServiceGroupCode(e.target.value || null)}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Service Group Description</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.serviceGroupServiceGroupDescription,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              placeholder="Service Group Description"
              value={serviceGroupName ?? ""}
              onChange={(e) => setServiceGroupName(e.target.value || null)}
              fullWidth
              error={nameFieldError}
              helperText={nameFieldError ? nameFieldErrorMessage : ""}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Service Group
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Service Groups Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={DNDFieldsEnum.SERVICEGROUPCODE}
              name={DNDFieldsEnum.SERVICEGROUPNAME}
            />
          ) : (
            <Typography variant="body2">No Service Groups Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaServiceGroups;
